'use client'

import * as React from 'react'

import { cn } from '@/lib/utils'

import { EASE, HAIRLINE, PANEL, SectionHead, useReveal } from './shared'

/* The product surface. Everything inside the window is live DOM — hairline
   rules, tabular figures, real column structure — not a screenshot and not a
   card *about* a screenshot. It stays crisp at any DPI, follows the theme,
   and costs nothing to load.

   The shared template renders the four UDC features as four equal boxes in a
   `lg:grid-cols-4`. Here the same four claims are the thing itself: three
   panes in one window plus the access model, which gets its own section. */

type Pane = {
  id: string
  tab: string
  title: string
  note: string
}

const PANES: Pane[] = [
  {
    id: 'lifecycle',
    tab: 'Lifecycle',
    title: 'One record, enquiry to alumnus',
    note: 'No stage hands off to a new system. The student ID issued at enquiry is the ID printed on the degree.',
  },
  {
    id: 'flow',
    tab: 'Data flow',
    title: 'One event, every module current',
    note: 'A single fee receipt, and everything downstream of it, without a reconciliation step.',
  },
  {
    id: 'leadership',
    tab: 'Leadership',
    title: 'The board pack, already written',
    note: 'Live institutional position. Nothing here was collated by hand.',
  },
]

/* ─── Pane A: the lifecycle rail ──────────────────────────────────────── */
const STAGES = [
  { name: 'Enquiry', count: '4,812' },
  { name: 'Application', count: '3,140' },
  { name: 'Admission', count: '1,206' },
  { name: 'Academics', count: '1,198' },
  { name: 'Examination', count: '1,198' },
  { name: 'Degree', count: '1,164' },
]

function LifecyclePane({ on }: { on: boolean }) {
  return (
    <div className="flex flex-1 flex-col">
      {/* The rail: one hairline through six nodes. Horizontal from sm up,
          a vertical spine on phones — rotated, never degraded into cards. */}
      <div className="relative">
        {/* horizontal rail */}
        <div
          aria-hidden
          className="absolute left-0 right-0 top-[7px] hidden h-px origin-left bg-border/70 sm:block"
          style={{
            transform: on ? 'scaleX(1)' : 'scaleX(0)',
            transition: `transform 900ms ${EASE} 80ms`,
          }}
        />
        {/* vertical rail (phones) */}
        <div
          aria-hidden
          className="absolute bottom-3 left-[7px] top-3 w-px origin-top bg-border/70 sm:hidden"
          style={{
            transform: on ? 'scaleY(1)' : 'scaleY(0)',
            transition: `transform 900ms ${EASE} 80ms`,
          }}
        />

        <ol className="relative grid grid-cols-1 gap-y-4 sm:grid-cols-6 sm:gap-y-0">
          {STAGES.map((s, i) => (
            <li
              key={s.name}
              className="flex items-center gap-3 sm:block"
              style={{
                opacity: on ? 1 : 0,
                transform: on ? 'none' : 'translateY(6px)',
                transition: `opacity 400ms ${EASE} ${140 + i * 80}ms, transform 400ms ${EASE} ${140 + i * 80}ms`,
              }}
            >
              {/* `block` matters: at sm+ the row becomes `display:block`, and
                  width/height do not apply to an inline span. */}
              <span
                aria-hidden
                className="block size-[15px] shrink-0 rounded-full border border-emerald-600/40 bg-background dark:border-primary-active/30"
              />
              <div className="min-w-0 sm:mt-3">
                <div className="text-[12px] font-medium leading-none text-foreground sm:text-[13px]">
                  {s.name}
                </div>
                <div className="mt-1.5 text-[11px] tabular-nums text-muted-foreground sm:mt-2 sm:text-xs">
                  {s.count}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* What survives every stage transition. This is the substance behind
          "end-to-end", and it keeps the pane the same height as its siblings
          so the window never resizes when you change tabs. */}
      <div className="mt-8">
        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          Carried through, stage to stage
        </div>

        <ul className="mt-3">
          {CARRIED.map((c, i) => (
            <li
              key={c.what}
              className={cn(
                'grid grid-cols-1 gap-1 border-b py-3 sm:grid-cols-[minmax(0,132px)_1fr] sm:items-baseline sm:gap-6',
                HAIRLINE
              )}
              style={{
                opacity: on ? 1 : 0,
                transform: on ? 'none' : 'translateY(6px)',
                transition: `opacity 380ms ${EASE} ${640 + i * 90}ms, transform 380ms ${EASE} ${640 + i * 90}ms`,
              }}
            >
              <span className="text-[12px] font-medium text-foreground sm:text-[13px]">
                {c.what}
              </span>
              <span className="text-[11px] leading-snug text-muted-foreground sm:text-[13px]">
                {c.how}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* mt-auto: the panes share one grid cell, so the tallest sets the
          window height. Pinning each pane's footer to the bottom turns the
          slack into deliberate breathing room instead of dead space. */}
      <div className="mt-auto flex flex-wrap gap-x-6 gap-y-2 pt-6">
        <Fact label="Records re-entered between stages" value="0" />
        <Fact label="Student identifiers in play" value="1" />
      </div>
    </div>
  )
}

const CARRIED = [
  {
    what: 'Identity',
    how: 'One student ID, issued at enquiry, printed on the degree, never reallocated.',
  },
  {
    what: 'Documents',
    how: 'Verified once at admission, reused at examination, convocation and audit.',
  },
  {
    what: 'Ledger',
    how: 'Every fee event posted against the same record, in sequence, immutably.',
  },
  {
    what: 'Attainment',
    how: 'Internals, attendance and results accumulate into the transcript as they happen.',
  },
]

/* ─── Pane B: one event, fanned out ───────────────────────────────────── */
const FLOW = [
  {
    module: 'Accounts',
    wrote: 'Ledger entry posted, receipt numbered',
    ms: '12 ms',
  },
  { module: 'LMS', wrote: 'Semester 5 portal access released', ms: '40 ms' },
  { module: 'Cloud', wrote: 'Replicated to two regions', ms: '90 ms' },
  {
    module: 'AI Engine',
    wrote: 'Defaulter forecast recalculated',
    ms: '210 ms',
  },
  { module: 'HRMS', wrote: 'No change (not in scope)', ms: '·' },
  { module: 'Payroll', wrote: 'No change (not in scope)', ms: '·' },
]

function FlowPane({ on }: { on: boolean }) {
  return (
    <div className="flex flex-1 flex-col">
      {/* The event */}
      <div
        className={cn(
          'flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-xl border px-4 py-3',
          HAIRLINE,
          'bg-emerald-600/[0.05] dark:bg-primary-active/[0.05]'
        )}
        style={{
          opacity: on ? 1 : 0,
          transition: `opacity 350ms ${EASE} 60ms`,
        }}
      >
        <span className="text-[10px] uppercase tracking-[0.16em] text-emerald-600 dark:text-primary-active">
          Event
        </span>
        <span className="text-[12px] font-medium tabular-nums text-foreground sm:text-[13px]">
          Fee receipt #24118 · ₹86,400
        </span>
        <span className="text-[11px] text-muted-foreground sm:text-xs">
          B.Tech CSE · Semester 5 · counter 2
        </span>
      </div>

      {/* What it wrote, per module */}
      <ul className="mt-1">
        {FLOW.map((r, i) => {
          // '·' is the no-value marker in the ms column; idle rows key off it.
          const idle = r.ms === '·'
          return (
            <li
              key={r.module}
              className={cn(
                'flex items-center gap-3 border-b py-3 sm:gap-5',
                HAIRLINE
              )}
              style={{
                opacity: on ? 1 : 0,
                transform: on ? 'none' : 'translateX(-6px)',
                transition: `opacity 380ms ${EASE} ${160 + i * 90}ms, transform 380ms ${EASE} ${160 + i * 90}ms`,
              }}
            >
              <span
                aria-hidden
                className={cn(
                  'size-1.5 shrink-0 rounded-full',
                  idle
                    ? 'bg-foreground/15'
                    : 'bg-emerald-600 dark:bg-primary-active'
                )}
              />
              <span className="w-[68px] shrink-0 text-[12px] font-medium text-foreground sm:w-[92px] sm:text-[13px]">
                {r.module}
              </span>
              <span
                className={cn(
                  'min-w-0 flex-1 text-[11px] leading-snug sm:text-[13px]',
                  idle ? 'text-muted-foreground/60' : 'text-muted-foreground'
                )}
              >
                {r.wrote}
              </span>
              <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground/70 sm:text-xs">
                {r.ms}
              </span>
            </li>
          )
        })}
      </ul>

      <div className="mt-auto flex flex-wrap gap-x-6 gap-y-2 pt-6">
        <Fact label="Manual re-entries triggered" value="0" />
        <Fact label="Reconciliation jobs queued" value="0" />
      </div>
    </div>
  )
}

/* ─── Pane C: the leadership view ─────────────────────────────────────── */
const KPIS = [
  {
    label: 'Admissions confirmed, 2026 cycle',
    value: '1,206',
    sub: 'of 1,240 sanctioned',
  },
  {
    label: 'Fee collected, current semester',
    value: '92.4%',
    sub: '₹9.71 Cr of ₹10.51 Cr',
  },
  {
    label: 'Attendance posted today',
    value: '98.1%',
    sub: '412 of 420 sessions',
  },
  {
    label: 'Payroll run, this month',
    value: 'Cleared',
    sub: '318 staff · PF, ESI & TDS filed',
  },
  {
    label: 'NAAC evidence completeness',
    value: '100%',
    sub: 'Criteria 1 to 7 mapped',
  },
]

function LeadershipPane({ on }: { on: boolean }) {
  return (
    <div className="flex flex-1 flex-col">
      <ul>
        {KPIS.map((k, i) => (
          <li
            key={k.label}
            className={cn(
              'flex items-baseline justify-between gap-4 border-b py-3.5',
              HAIRLINE
            )}
            style={{
              opacity: on ? 1 : 0,
              transform: on ? 'none' : 'translateY(6px)',
              transition: `opacity 380ms ${EASE} ${80 + i * 90}ms, transform 380ms ${EASE} ${80 + i * 90}ms`,
            }}
          >
            <div className="min-w-0">
              <div className="text-[12px] font-medium leading-snug text-foreground sm:text-[13px]">
                {k.label}
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground/70 sm:text-xs">
                {k.sub}
              </div>
            </div>
            <div className="font-heading shrink-0 text-lg font-normal tabular-nums leading-none text-foreground sm:text-xl">
              {k.value}
            </div>
          </li>
        ))}
      </ul>

      {/* The 6× claim, drawn as a ratio rather than asserted as a stat: the
          same board pack, before and after, at true relative width. */}
      <div className="mt-auto pt-7">
        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          Time to assemble this page
        </div>

        <div className="mt-3 space-y-2.5">
          <Ratio
            label="Collated by hand"
            width="100%"
            on={on}
            delay={620}
            muted
          />
          <Ratio label="With UDC" width="16.6667%" on={on} delay={760} />
        </div>
      </div>
    </div>
  )
}

function Ratio({
  label,
  width,
  on,
  delay,
  muted,
}: {
  label: string
  width: string
  on: boolean
  delay: number
  muted?: boolean
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-[104px] shrink-0 text-[11px] text-muted-foreground sm:w-[128px] sm:text-xs">
        {label}
      </span>
      <span className="relative h-2 flex-1 overflow-hidden rounded-full bg-foreground/[0.06]">
        <span
          className={cn(
            'absolute inset-y-0 left-0 rounded-full',
            muted ? 'bg-foreground/20' : 'bg-emerald-600 dark:bg-primary-active'
          )}
          style={{
            width: on ? width : '0%',
            transition: `width 800ms ${EASE} ${delay}ms`,
          }}
        />
      </span>
    </div>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="font-heading text-base font-normal tabular-nums text-foreground">
        {value}
      </span>
      <span className="ml-2 text-[11px] text-muted-foreground sm:text-xs">
        {label}
      </span>
    </div>
  )
}

/* ─── The window ──────────────────────────────────────────────────────── */
export function UdcConsole() {
  const [active, setActive] = React.useState(0)
  const section = useReveal<HTMLDivElement>(0.08)
  const tabRefs = React.useRef<(HTMLButtonElement | null)[]>([])

  const onKeyDown = (e: React.KeyboardEvent) => {
    const last = PANES.length - 1
    let next = -1
    if (e.key === 'ArrowRight') next = active === last ? 0 : active + 1
    if (e.key === 'ArrowLeft') next = active === 0 ? last : active - 1
    if (e.key === 'Home') next = 0
    if (e.key === 'End') next = last
    if (next < 0) return
    e.preventDefault()
    setActive(next)
    tabRefs.current[next]?.focus()
  }

  return (
    <section className="pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <SectionHead
          eyebrow="The product"
          title="One window on the whole institution."
          deck="Three surfaces from the running platform. The data underneath is the same in all of them. That is the only reason a page like this can exist."
        />

        <div ref={section.ref} className="mt-10 sm:mt-12">
          <div
            className={cn(
              'overflow-hidden rounded-[20px] sm:rounded-[24px]',
              PANEL
            )}
            style={{
              opacity: section.shown ? 1 : 0,
              transform: section.shown ? 'none' : 'translateY(16px)',
              transition: `opacity 700ms ${EASE}, transform 700ms ${EASE}`,
            }}
          >
            {/* Window chrome: a location, a live marker, and the app's own
                navigation. No traffic-light dots. */}
            <div
              className={cn(
                'flex flex-col gap-3 border-b px-4 pt-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-6 sm:pt-4',
                HAIRLINE
              )}
            >
              <div className="flex min-w-0 items-center gap-2 pb-1 sm:pb-4">
                <span className="truncate rounded-md bg-foreground/[0.06] px-2 py-1 text-[10px] tabular-nums text-muted-foreground sm:text-[11px]">
                  udc.euclideum.com&nbsp;/&nbsp;{PANES[active]!.id}
                </span>
                <span className="flex shrink-0 items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground sm:text-[11px]">
                  <span
                    aria-hidden
                    className="size-1.5 rounded-full bg-emerald-600 dark:bg-primary-active"
                  />
                  Live
                </span>
              </div>

              <div
                role="tablist"
                aria-label="Console views"
                onKeyDown={onKeyDown}
                className="-mb-px flex gap-1 overflow-x-auto sm:gap-2"
              >
                {PANES.map((p, i) => {
                  const on = i === active
                  return (
                    <button
                      key={p.id}
                      ref={(el) => {
                        tabRefs.current[i] = el
                      }}
                      role="tab"
                      type="button"
                      id={`udc-tab-${p.id}`}
                      aria-selected={on}
                      aria-controls={`udc-panel-${p.id}`}
                      tabIndex={on ? 0 : -1}
                      onClick={() => setActive(i)}
                      className={cn(
                        'relative shrink-0 px-2.5 pb-3.5 pt-1 text-[12px] font-medium transition-colors duration-200 sm:px-3 sm:pb-4 sm:text-[13px]',
                        'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
                        'focus-visible:outline-emerald-600 dark:focus-visible:outline-primary-active',
                        on
                          ? 'text-foreground'
                          : 'text-muted-foreground hover:text-foreground/80'
                      )}
                    >
                      {p.tab}
                      <span
                        aria-hidden
                        className={cn(
                          'absolute inset-x-1.5 bottom-0 h-[2px] rounded-full transition-colors duration-200',
                          on
                            ? 'bg-emerald-600 dark:bg-primary-active'
                            : 'bg-transparent'
                        )}
                      />
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Panels share one grid cell, so the window is as tall as the
                tallest view and switching never shifts the page. */}
            <div className="grid p-5 sm:p-7 lg:p-9">
              {PANES.map((p, i) => {
                const on = i === active && section.shown
                return (
                  <div
                    key={p.id}
                    role="tabpanel"
                    id={`udc-panel-${p.id}`}
                    aria-labelledby={`udc-tab-${p.id}`}
                    aria-hidden={i !== active}
                    className={cn(
                      '[grid-area:1/1] flex flex-col transition-opacity [transition-duration:400ms]',
                      i === active
                        ? 'opacity-100'
                        : 'pointer-events-none opacity-0'
                    )}
                    style={{ transitionTimingFunction: EASE }}
                  >
                    <h3 className="font-heading text-lg font-normal leading-tight text-foreground sm:text-xl lg:text-2xl">
                      {p.title}
                    </h3>
                    <p className="mt-2 max-w-xl text-[13px] leading-relaxed text-muted-foreground sm:text-sm">
                      {p.note}
                    </p>

                    <div className="mt-7 flex flex-1 flex-col sm:mt-8">
                      {p.id === 'lifecycle' && <LifecyclePane on={on} />}
                      {p.id === 'flow' && <FlowPane on={on} />}
                      {p.id === 'leadership' && <LeadershipPane on={on} />}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
